/**
 * ConceptVisual - Container visual for concept blocks
 *
 * Concepts are formation + route templates. They render their child drawings
 * (route patterns) on the canvas. Similar to PlayVisual but concept-specific.
 *
 * Pure visual component - no editor state or handlers.
 */

import { BlockRenderer } from '../../BlockRenderer'
import type { BlockVisualProps } from '../../Types'
import type { ConceptBlock } from '@/types/blocks/canvas/Concept'
import type { DrawingBlock } from '@/types/blocks/canvas/Drawing'

export function ConceptVisual({ block }: BlockVisualProps) {
	// Type guard
	if (block.type !== 'concept') {
		console.error('ConceptVisual called with non-concept block:', block.type)
		return null
	}

	const conceptBlock = block as ConceptBlock

	// Check for resolved drawings in properties
	// API should embed drawings when returning concept
	const drawings = (conceptBlock.properties as Record<string, unknown>).__resolved_drawings as
		| DrawingBlock[]
		| undefined

	// No children state
	if (!drawings || drawings.length === 0) {
		return (
			<g className="concept-empty">
				<text
					x="0"
					y="0"
					fill="var(--muted-foreground)"
					fontSize="12"
					fontFamily="monospace"
				>
					Concept has no drawings
				</text>
			</g>
		)
	}

	// Render all drawings recursively
	return (
		<g className="concept-container" data-block-id={conceptBlock.id}>
			{drawings.map((drawing) => (
				<BlockRenderer key={drawing.id} block={drawing} />
			))}
		</g>
	)
}
